import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { DigitalKey, Skeleton } from '@/ds'
import type { KeyAccess } from '@/ds'
import { LockedKey, useKeyDiscrepancy } from '@/app/accesos/LockedKey'
import { useKeyOpener } from '@/app/accesos/useKeyOpener'
import { useAppNavigate } from '@/lib/query-params'
import { asyncValue } from '@/lib/useAsync'
import type { KeyStatus } from '@/services/accesos'
import type { Stay } from '@/services/reserva'
import { keyValidity, STEP_ROUTES } from '../steps'
import type { StayData } from '../useStayData'

interface Props {
  stay: Stay
  data: StayData
  lang: string
  now: Date
  /** `home`: llave completa; `compact`: accesos directos durante la estancia. */
  variant: 'home' | 'compact'
}

function toAccesses(key: KeyStatus, states: Record<string, string>, t: TFunction, withLabel: boolean): KeyAccess[] {
  return key.accesses
    .filter((a) => a.openable)
    .map((a) => ({ id: a.id, name: a.name, state: (states[a.id] as KeyAccess['state']) ?? 'active', ...(withLabel ? { label: t('v2.ds.digitalKey.abrir', { name: a.name.toLowerCase() }) } : {}) }))
}

/** Llave en el inicio (bundle B1 fases 2 y 3): todos los accesos que se pueden abrir están disponibles aquí. */
export function KeySection({ stay, data, lang, now, variant }: Props) {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const { states, open } = useKeyOpener(stay)
  const key = asyncValue(data.key)
  const accesses = key ? toAccesses(key, states, t, variant === 'home') : []
  useKeyDiscrepancy(stay, key, asyncValue(data.payment), asyncValue(data.deposit), data.payment.kind !== 'loading' && data.deposit.kind !== 'loading', now)

  if (data.key.kind === 'loading') return <Skeleton height={variant === 'compact' ? 96 : 200} radius={24} />
  if (!key || key.state === 'none' || key.state === 'expired') return null

  const onOpen = (id: string) => {
    const access = key.accesses.find((a) => a.id === id)
    if (access && access.intercom !== null) go(STEP_ROUTES.key, { state: { accessId: id } })
    else void open(id)
  }

  if (variant === 'compact') {
    if (key.state !== 'active' || accesses.length === 0) return null
    return <DigitalKey compact state="active" accesses={accesses} onOpen={onOpen} onRetry={onOpen} className="estancia__key-compact" />
  }

  if (key.state === 'locked') {
    if (data.payment.kind === 'loading' || data.deposit.kind === 'loading') return <Skeleton height={200} radius={24} />
    return <LockedKey stay={stay} keyStatus={key} payment={asyncValue(data.payment)} deposit={asyncValue(data.deposit)} now={now} origin="estancia" />
  }

  const validity = keyValidity(key, now, lang, t)
  return (
    <section className="estancia__key" aria-label={t('v2.ds.digitalKey.tuLlave')}>
      <DigitalKey state="active" accesses={accesses} onOpen={onOpen} onRetry={onOpen} {...(validity ? { validity } : {})} {...(stay.contact.supportPhone ? { phone: stay.contact.supportPhone } : {})} />
    </section>
  )
}
